/**
 * SceneCopy — three vertical columns of Live scene slots: COPY, PASTE and
 * CLEAR, driving the OP-SceneCopyPaste device over OSC.
 *
 * The device copies a whole row of Session clips from one scene into another
 * WITHOUT duplicating the scene, so scene numbering (and every per-scene MIDI
 * mapping) survives. COPY arms a scene into the device's clipboard; PASTE
 * writes the armed row into a scene. "COPY 3" then "PASTE 5" moves a row with
 * no mouse and no scene selection involved. CLEAR empties a scene's slots.
 *
 * COLUMNS, not fifteen independent buttons, because COPY and PASTE are one
 * stateful PAIR — the left column picks a source, the middle picks a
 * destination, and the armed highlight ties them together. CLEAR is a third
 * column rather than a mode precisely because it is destructive on its own:
 * it belongs in the button presses, where it is visible, not behind a toggle.
 *
 * CLEAR EMPTIES A SCENE'S CLIP SLOTS. IT DOES NOT DELETE THE SCENE. That is
 * the distinction the whole device is built around - removing a scene would
 * renumber everything below it and break per-scene MIDI mappings, so scene
 * count and order stay invariant and only slot contents change. Live's own
 * undo covers it.
 *
 * The armed highlight comes from `sceneCopy/session.ts` and is an OPTIMISTIC
 * mirror of the device's clipboard, not a readback — see that file's header
 * for what it can be wrong about and why it is still worth drawing.
 *
 * Labels are 1-based (Live's numbering). The 0-based wire conversion lives
 * in `sendSceneCopy` and nowhere else.
 *
 * Edit mode yields to the wrapper (select/drag); test mode fires. The item
 * defaults to GUARDED (PASTE is destructive) - see `onCellDown` - and since
 * DEC-216 a guard applies whenever edit mode is off, so every cell is a
 * two-tap by default, in rehearsal as well as on stage.
 *
 * VERIFIED ON THE IPAD 2026-08-30 (operator, deployed build): the columns
 * render, both dispatch, and the COPY highlight clears on a paste. That
 * closes the primary-device gate for THIS SURFACE.
 *
 * THE DEVICE SIDE IS ALSO CONFIRMED, for this path only. The operator
 * verified 2026-08-30 that clips actually MOVE in Live when driven from
 * this item - an observed clip move, not a 200 back from the relay. That
 * supersedes the device's own "NEVER SMOKE-TESTED IN LIVE" warning
 * (OPM4LBuilder `corpus/23-OP-SceneCopyPaste-v0.3/build-log.md`) for the
 * merge-mode explicit-index path, and only that; the addendum appended to
 * that build log records exactly what is and is not covered. The off-by-one
 * between the 1-based labels and the 0-based wire - the live-fire risk on
 * this device - is now confirmed correct in both directions against a real
 * set, not just against the smoke.
 *
 * PASTE IS ALWAYS MERGE as of device v0.4 - only slots the SOURCE occupied
 * get written, and clips already in the target on other tracks survive.
 * There is no longer a mode to get this wrong: the old MIRROR toggle, which
 * deleted target clips wherever the source row was empty, is GONE from the
 * device. Its own reason for removing it is worth keeping in view - it was a
 * stored, MIDI-mappable front-panel toggle that could silently destroy clips
 * if it was ever left on, and it had never been run in a real set. Earlier
 * revisions of this file described mirror as "deliberately unreachable from
 * this surface"; it is now unreachable from ANY surface.
 *
 * CLEAR (third column, device v0.4) is what replaced it: the same capability,
 * explicit and visible. To mirror a scene, CLEAR the target, then COPY and
 * PASTE - the destruction is in the button presses rather than in a mode.
 *
 * DELETE ALL (device v1.2, 2026-09-11) is an OPT-IN bar under the grid, off
 * unless `showDeleteAll` is set. It empties every clip slot on EVERY scene,
 * and unlike every other control here its confirm lives in the DEVICE: two
 * `clear_all` packets inside its 3s window, the first arming and the second
 * executing. This file therefore MIRRORS that state machine rather than
 * implementing one, and the bar deliberately does NOT go through `guard` -
 * see `onDeleteAllDown` for why stacking the two would make it a four-tap
 * control, and for the invariant that a dark bar means a disarmed device.
 *
 * The wire prefix is deliberately `/OP-AbletonSceneCopy` even though the
 * device file is now `OP-SceneCopyPaste.amxd` and its companion JS was
 * renamed to `SceneCopyPaste.js` in v0.4 - the OSC prefix itself is the part
 * held frozen ("wire-protocol-freeze convention, same as OP-Transpose/
 * OP-ClipSustainStop"). Re-verified against the v0.4 build: `udpreceive
 * 11240` and `var OSC = "/OP-AbletonSceneCopy"` both unchanged.
 * See `sendSceneCopy` for the full reasoning.
 */

import { For, Show, createSignal, onCleanup } from 'solid-js';
import { editOwnsGesture, type SceneCopyItem, type LayoutColor } from '../layout/store.ts';
import { armedScene, noteArmed, clearArmed } from '../sceneCopy/session.ts';
import { sendSceneCopy, sendSceneClearAll, SCENE_CLEAR_ALL_MIN_GAP_MS } from '../opbridge/dispatch.ts';
import { fanoutTargets } from '../opbridge/dispatch.ts';
import { createDiscreteGuard, guardEnabled } from '../performance/guard.ts';
import { TrayTitle, trayAttrs } from './TrayTitle.tsx';

/**
 * How long our DELETE ALL bar shows TAP AGAIN before it withdraws the arm.
 *
 * DELIBERATELY SHORTER THAN THE DEVICE'S 3000ms, and the margin is the whole
 * point. The device's window opens when the packet ARRIVES; ours opens when
 * we SEND, so the device always lapses slightly after us. Expiring first
 * means our cancel reaches the device while it is still armed, so the two
 * agree. The other ordering is the dangerous one: if the device lapsed first
 * and our bar still read TAP AGAIN, the operator's "confirm" tap would land
 * as a FRESH ARM - the UI would say a delete just happened when nothing did,
 * and the set would then be one stray tap from being emptied with no bar lit.
 */
const DELETE_ALL_UI_ARM_MS = 2400;

interface SceneCopyProps {
  item: SceneCopyItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function SceneCopy(props: SceneCopyProps) {
  // Guard defaults ON for this kind, unlike Transpose/Capo where it is
  // opt-in. PASTE overwrites clips in the live set with no OSC undo path, so
  // the two-tap arm is the default rather than a setting you have to find.
  // `guardEnabled` owns that default so the Inspector's checkbox cannot
  // drift out of agreement with the behaviour.
  const guard = createDiscreteGuard(() => guardEnabled(props.item));

  /** 1-based Live scene numbers this item addresses. */
  const scenes = (): number[] => {
    const out: number[] = [];
    for (let i = 0; i < props.item.count; i++) out.push(props.item.firstScene + i);
    return out;
  };

  function onCellDown(verb: 'copy' | 'paste' | 'clear', scene: number, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();
    // Guard key is per verb+scene, so arming "PASTE 5" does not also arm
    // "PASTE 4" or the COPY cell on the same row.
    if (!guard.gate(`${verb}-${scene}`)) return;
    for (const target of fanoutTargets(props.item.bridgeIds)) {
      void sendSceneCopy(verb, scene, target);
    }
    // Optimistic — we record what we asked for. A COPY the device rejected
    // (no clips, bad index) will still light here; pressing COPY again is
    // the recovery, which is cheaper than no feedback at all mid-set.
    if (verb === 'copy') noteArmed(scene);
    // CLEAR empties the scene's slots. The device disarms its clipboard when
    // the scene it just emptied WAS the armed one, so a following PASTE
    // cannot silently copy nothing - mirror that here.
    //
    // The mirroring is APPROXIMATE and deliberately errs toward "not armed".
    // The device's real condition is `armedIdx === idx && cleared > 0`; we
    // cannot see `cleared`, so clearing an already-empty armed scene disarms
    // the UI while the device stays armed. The row was empty either way, so
    // the paste it protects against would have copied nothing. The opposite
    // error - leaving the UI lit after the device disarmed - is the harmful
    // one, because the operator would press PASTE and get silence with no
    // explanation.
    if (verb === 'clear' && armedScene() === scene) clearArmed();
    // A paste completes the operation, so the highlight goes out (operator
    // ask 2026-08-30). The highlight means "copied, waiting to be placed" -
    // once it has been placed there is nothing pending, and a lit cell left
    // over from the last move is noise on a stage surface.
    //
    // NOTE the device does NOT clear its own clipboard here - its wire has
    // no disarm verb at all (copy/paste/copy_to/mode/sethost/setport/rescan/
    // status), so this is a UI state change only. Pressing PASTE again with
    // nothing lit WILL still paste the last-copied row. That is why the
    // guard matters: the two-tap (always on, DEC-216) is what actually stops a
    // stray press, not the absence of a highlight.
    if (verb === 'paste') clearArmed();
  }

  // ─── DELETE ALL (device v1.2) ────────────────────────────────────────
  //
  // TWO PRESSES, AND THEY ARE THE DEVICE'S GUARD, NOT OURS. Each tap here
  // sends exactly ONE `clear_all` packet: the first arms the device and
  // deletes nothing, the second (inside its 3s window) executes. This bar
  // therefore MIRRORS a device state machine rather than implementing one,
  // which is why it does not go through `guard` like every other cell.
  //
  // ⚠️ IT MUST NOT GO THROUGH `guard`, and that is the trap in this feature.
  // `createDiscreteGuard` swallows the first tap WITHOUT SENDING and fires on
  // the second - so stacked on top of the device's own two-press arm it would
  // take FOUR taps to delete anything, with the device's 3s window expiring
  // somewhere in the middle. The control would read as broken, and the
  // obvious "fix" - sending twice per tap - would defeat the guard entirely.
  // The device's arm is also the STRONGER of the two: it covers the panel
  // button and a MIDI mapping as well as our packet, where ours covers only
  // taps on this bar.
  const [deleteArmed, setDeleteArmed] = createSignal(false);
  let armedAt = 0;
  let armTimer: number | undefined;

  /** One `clear_all` packet to every bridge this item fans out to. Never
   *  throws - `sendSceneClearAll` reports an offline bridge as a FireResult. */
  async function pressAll(intent: 'press' | 'cancel'): Promise<void> {
    await Promise.all(
      fanoutTargets(props.item.bridgeIds).map((t) => sendSceneClearAll(intent, t)),
    );
  }

  /**
   * THE INVARIANT THIS BAR HOLDS: when it is dark, the device is not armed.
   *
   * Every path back to dark - the confirm, the timeout, and unmounting -
   * sends an explicit `clear_all 0`, because we cannot see the device's real
   * arm state (it emits `/all_armed` on 11204 and OPButtons has no inbound
   * OSC path). Without this, a lost or late FIRST packet turns the operator's
   * CONFIRM tap into a fresh ARM: the bar goes dark claiming a delete
   * happened, nothing was deleted, and the set is left one stray tap from
   * being emptied with nothing lit to say so. The cancel costs one packet and
   * is a no-op on a device that is not armed (`doClearAll`: `if (allArmedAt)`).
   *
   * After a confirm the cancel is sent AFTER that press resolves, so OPBridge
   * relays them in order: the device either already executed and disarmed
   * itself (cancel no-ops) or was only armed by that press (cancel withdraws
   * it). Both land on "not armed", which is the invariant.
   */
  function settleDark(alsoCancel: boolean): void {
    if (armTimer !== undefined) { clearTimeout(armTimer); armTimer = undefined; }
    armedAt = 0;
    setDeleteArmed(false);
    if (alsoCancel) void pressAll('cancel');
  }
  onCleanup(() => { settleDark(armedAt !== 0); });

  function onDeleteAllDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    e.stopPropagation();

    if (deleteArmed()) {
      // A tap this close to the arming one is a fat-fingered double-tap, not
      // two decisions. Swallow it entirely - do not send, do not disarm - so
      // the bar stays lit and the operator's deliberate second tap still
      // works.
      //
      // ⚠️ This gap is now a FLOOR the device also enforces, not a courtesy.
      // In device v1.2-rc1 the `clear_all` OSC verb was un-gated and this was
      // the ONLY thing stopping a double-tap emptying a set; rc2 gave the
      // relayed verb the panel button's own 250ms debounce, so a confirm
      // under 250ms is now swallowed DEVICE-side too - silently, with no
      // packet rejected. Tuning this below 250 would make the bar dead rather
      // than fast. See `SCENE_CLEAR_ALL_MIN_GAP_MS`.
      if (Date.now() - armedAt < SCENE_CLEAR_ALL_MIN_GAP_MS) return;
      settleDark(false);
      void (async () => {
        await pressAll('press');   // the confirm - this is what deletes
        await pressAll('cancel');  // hold the invariant above
      })();
      // The device drops its clipboard unconditionally after a delete-all
      // ("there is no scene left that could still hold what was armed"), so
      // the COPY highlight goes out too - unlike a per-scene CLEAR, which
      // only disarms when it emptied the armed scene.
      clearArmed();
      return;
    }

    armedAt = Date.now();
    setDeleteArmed(true);
    void pressAll('press');
    armTimer = window.setTimeout(() => {
      armTimer = undefined;
      settleDark(true);
    }, DELETE_ALL_UI_ARM_MS);
  }

  return (
    <div
      {...trayAttrs(props.item)}
      class="scene-copy"
      data-token={COLOR_TOKEN[props.item.color]}
      data-delete-all={props.item.showDeleteAll ? 'true' : 'false'}
      style={{
        '--sc-rows': String(props.item.count),
        '--sc-fuse-ms': `${DELETE_ALL_UI_ARM_MS}ms`,
      }}
    >
      <TrayTitle item={props.item} />
      <For each={scenes()}>
        {(scene) => (
          <>
            <button
              type="button"
              class="scene-copy-cell"
              data-role="copy"
              data-armed={guard.armedKey() === `copy-${scene}` ? 'true' : 'false'}
              data-active={armedScene() === scene ? 'true' : 'false'}
              data-scene={scene}
              onPointerDown={(e) => onCellDown('copy', scene, e)}
              title={`Arm scene ${scene} into the clipboard (COPY)`}
            >
              <span class="scene-copy-verb">COPY</span>
              <span class="scene-copy-num">{scene}</span>
            </button>
            <button
              type="button"
              class="scene-copy-cell"
              data-role="paste"
              data-armed={guard.armedKey() === `paste-${scene}` ? 'true' : 'false'}
              data-active="false"
              data-scene={scene}
              onPointerDown={(e) => onCellDown('paste', scene, e)}
              title={
                armedScene() === null
                  ? `Write the clipboard into scene ${scene} (PASTE) - nothing copied yet`
                  : `Write scene ${armedScene()}'s clips into scene ${scene} (PASTE) - overwrites`
              }
            >
              <span class="scene-copy-verb">PASTE</span>
              <span class="scene-copy-num">{scene}</span>
            </button>
            <button
              type="button"
              class="scene-copy-cell"
              data-role="clear"
              data-armed={guard.armedKey() === `clear-${scene}` ? 'true' : 'false'}
              data-active="false"
              data-scene={scene}
              onPointerDown={(e) => onCellDown('clear', scene, e)}
              title={`Empty every clip slot on scene ${scene} (CLEAR) - destructive; the scene itself is not removed, so scene numbers and MIDI maps are unaffected. Live's own undo covers it.`}
            >
              <span class="scene-copy-verb">CLEAR</span>
              <span class="scene-copy-num">{scene}</span>
            </button>
          </>
        )}
      </For>
      {/* Opt-in, and off by default - see `SceneCopyItem.showDeleteAll`. It
          spans all three columns because it is not a scene: COPY/PASTE/CLEAR
          each act on ONE numbered row, this acts on the whole set, and giving
          it a column would put it in the same reading line as a per-scene
          verb. */}
      <Show when={props.item.showDeleteAll}>
        <button
          type="button"
          class="scene-copy-cell scene-copy-all"
          data-role="delete-all"
          data-armed={deleteArmed() ? 'true' : 'false'}
          data-active="false"
          onPointerDown={onDeleteAllDown}
          title={
            deleteArmed()
              ? 'Tap again to EMPTY EVERY CLIP SLOT ON EVERY SCENE. Waiting a moment cancels it.'
              : 'Empty every clip slot on every scene. Two taps: the first arms, the second deletes.'
          }
        >
          <span class="scene-copy-verb">
            {deleteArmed() ? 'TAP AGAIN' : 'DELETE ALL'}
          </span>
          {/* The fuse is the only liveness proof this bar has. An armed
              state with no visible clock cannot be told apart from a stuck
              one, and the recovery for the two is opposite: wait, or tap.
              CSS-animated off `--sc-fuse-ms` rather than a ticking signal,
              so a bar counting down never re-renders the grid it sits in. */}
          <Show when={deleteArmed()}>
            <span class="scene-copy-fuse" />
          </Show>
        </button>
      </Show>
    </div>
  );
}
